import { faSearch } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FC, useEffect, useRef, useState } from 'react'
import { Column } from '@tanstack/react-table'
import { twMerge } from 'tailwind-merge'
import { useDebounce } from '@hooks/useDebounce'

interface Props {
  column: Column<unknown>
  inputClassName?: string
  placeholder: string
}

export const TableTextFilterField: FC<Props> = ({
  column,
  inputClassName,
  placeholder,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const columnValue = (column.getFilterValue() as string | undefined) ?? ''

  // Re-filtering re-renders every row of the table, which on a big table (the
  // Users page runs to thousands) costs far more than one keystroke's worth of
  // time. Keep what's typed in local state so the input stays responsive, and
  // only hand it to the table once typing pauses.
  const [value, setValue] = useState(columnValue)
  const debouncedValue = useDebounce(value)

  // The last value the input and the column agreed on, so neither direction of
  // the sync below undoes the other.
  const syncedValue = useRef(columnValue)

  useEffect(() => {
    if (debouncedValue === syncedValue.current) return

    syncedValue.current = debouncedValue
    column.setFilterValue(debouncedValue)
  }, [debouncedValue, column])

  // Pick up filter changes made elsewhere — e.g. the Approvals admin table's
  // "reset filters" button.
  useEffect(() => {
    if (columnValue === syncedValue.current) return

    syncedValue.current = columnValue
    setValue(columnValue)
  }, [columnValue])

  return (
    <div
      className="border-neutral-400 border-[1.5px] rounded font-normal flex items-center px-3 gap-1.5 bg-white"
      // Don't propagate clicks, otherwise we'll trigger column sorting
      onClick={(e) => e.stopPropagation()}
    >
      <FontAwesomeIcon
        icon={faSearch}
        className="text-neutral-500"
        onClick={() => inputRef.current?.focus()}
      />
      <input
        ref={inputRef}
        type="text"
        className={twMerge(
          'grow border-none bg-transparent focus:ring-0 text-xs px-0 py-2 placeholder:text-neutral-600 text-neutral-900',
          inputClassName,
        )}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        value={value}
      />
    </div>
  )
}
